import React, { useState } from "react";
import StarRoundeIcon from "@mui/icons-material/StarRounded";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Chip,
  Button,
  Box,
  SvgIcon,
} from "@mui/material";
import { FavoriteBorder, Favorite } from "@mui/icons-material";
import "./SimilarProviderCard.scss";
import {
  ProviderWithContactInfo,
  FileInfo,
  Service,
} from "../details-page.types";
import ProviderCardContactModal from "../../../common/components/provider-card-contactus-modal/ProviderCardContactModal";
import { toggleFavoriteProvider } from "../../../api/details-page.api";
import { ReactComponent as NoImgIcon } from "../../../assets/icons/no-img-icon.svg";
import store from "../../../store";
import { truncateWords } from "../../../utils/utils";

type SimilarProviderCardProps = {
  provider: ProviderWithContactInfo;
  onViewDetails: (id: number) => void;
  onContactInfo: (id: number) => void;
};

const SimilarProviderCard: React.FC<SimilarProviderCardProps> = ({
  provider,
  onViewDetails,
  onContactInfo,
}) => {
  const {
    id,
    businessName,
    businessType,
    description,
    location,
    avgRating,
    isFavorite,
    services,
    address,
    fileInfo,
    contactInfo,
    email,
    phoneNumber,
  } = provider;

  const imageUrl = fileInfo?.find((f) => f.mediaType === "image")?.url || "";

  const tags = services?.map((s) => s.name) || [];
  const isLoggedIn = store.getState()?.session?.isLoggedIn || false;

  // STATES
  const [openModal, setOpenModal] = useState(false);
  const [isFavorited, setIsFavorited] = useState(isFavorite);
  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const handleFavoriteProvider = async () => {
    if (!isLoggedIn) return;
    const previous = isFavorited;
    setIsFavorited((prev) => !prev);

    try {
      const response = await toggleFavoriteProvider(Number(id));
      if (response.data?.success) {
        setIsFavorited(response.data?.data?.isFavorite);
      }
    } catch (error) {
      console.error("ERROR FAVORITING PROVIDER::", error);
      setIsFavorited(previous);
    }
  };

  return (
    <Card className="similar-provider-card">
      <div className="similar-provider-card__image-wrapper">
        {imageUrl ? (
          <CardMedia
            component="img"
            height="180"
            image={imageUrl}
            alt={businessName}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "180px",
              background: "#eaeaea",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "1rem",
              borderRadius: "6px 6px 0 0",
              color: "#555",
              backgroundColor: "#F9FAFC",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            {" "}
            <SvgIcon
              component={NoImgIcon}
              style={{
                height: "48px",
                width: "48px",
              }}
              inheritViewBox
            />
            <Typography>No Image Available</Typography>
          </Box>
        )}

        {imageUrl && (
          <>
            <Typography className="similar-provider-card__type-label">
              {businessType}
            </Typography>

            {/* {isLoggedIn && (
              <IconButton
                className="similar-provider-card__favorite-icon"
                size="small"
                onClick={handleFavoriteProvider}
              >
                {isFavorited ? (
                  <Favorite className="filled" />
                ) : (
                  <FavoriteBorder />
                )}
              </IconButton>
            )} */}
          </>
        )}
      </div>

      <CardContent className="similar-provider-card__content">
        <Box className="similar-provider-card__main">
          <Box className="similar-provider-card__header">
            <Box className="similar-provider-card__header-top">
              <Typography variant="h6" className="similar-provider-card__title">
                {truncateWords(businessName ?? "", 10)}
              </Typography>
              <Box className="similar-provider-card__rating">
                {avgRating && (
                  <StarRoundeIcon
                    className="similar-provider-card__rating-icon"
                    fontSize="small"
                  />
                )}
                <Typography className="similar-provider-card__rating-value">
                  {avgRating !== null && avgRating !== undefined
                    ? avgRating?.toFixed(1)
                    : ""}
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="body2"
              className="similar-provider-card__location"
            >
              {location}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          className="similar-provider-card__description"
        >
          {truncateWords(description ?? "", 10)}
        </Typography>

        <Box className="similar-provider-card__tags">
          {tags.length > 0 && (
            <Chip
              label={tags[0]}
              size="small"
              className="similar-provider-card__chip"
            />
          )}
          {tags.length > 1 && (
            <Chip
              label={`+${tags.length - 1}`}
              size="small"
              className="similar-provider-card__chip"
            />
          )}
        </Box>

        <Box className="similar-provider-card__actions">
          <Button
            variant="outlined"
            onClick={() => onViewDetails(id)}
            className="similar-provider-card__actions-view"
          >
            View Details
          </Button>
          <Button
            variant="contained"
            onClick={handleOpen}
            className="similar-provider-card__actions-contact"
          >
            Contact Info
          </Button>
        </Box>
      </CardContent>

      <ProviderCardContactModal
        open={openModal}
        onClose={handleClose}
        title={businessName}
        latitude={contactInfo.latitude}
        longitude={contactInfo.longitude}
        phoneNumber={phoneNumber}
        email={email}
        address={address}
        contactInfo={provider.contactInfo}
      />
    </Card>
  );
};

export default SimilarProviderCard;
